import React, { useState, useEffect, Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { VoiceInput } from '@/components/VoiceInput';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FileText, PlayCircle, Image as ImageIcon, CheckCircle, Camera, AlertCircle, Plus, Minus, ExternalLink } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { BrandProvider } from '@/contexts/BrandContext';
import api from '@/lib/api';
import { toast } from 'sonner';
// Lazy-loaded: camera + OCR/canvas logic only loads when scan mode is used.
const CardScanner = lazy(() => import('@/components/CardScanner'));

const COUNTRY_CODES = [
  { code: '+1' },
  { code: '+44' },
  { code: '+91' },
  { code: '+61' },
  { code: '+86' },
  { code: '+49' },
  { code: '+33' },
  { code: '+52' },
  { code: '+55' },
  { code: '+81' },
  { code: '+82' },
  { code: '+65' },
  { code: '+971' },
];

const BANT_QUESTIONS = [
  { key: 'bant_budget', label: 'Is a budget allocated to address these IT challenges?', options: ['Yes', 'No', 'Not sure'] },
  { key: 'bant_role', label: "What's your role in the decision?", options: ['Decision maker', 'Influencer', 'Evaluator', 'End user', 'No role'] },
  { key: 'bant_committee', label: 'Are you on the RFP / buying committee?', options: ['Yes', 'No', 'Not sure'] },
  { key: 'bant_need_stage', label: 'Where are you in addressing these IT challenges?', options: ['Actively solving', 'Exploring options', 'Just curious'] },
  { key: 'bant_existing_solution', label: 'Do you have a solution in place today?', options: ['Yes, OEM Solution', 'Yes, In-house solution', 'No'] },
  { key: 'bant_timeline', label: 'When are you looking to decide?', options: ['1-3 months', '4-6 months', '7+ months', 'Just exploring'] },
];
const BANT_KEYS = BANT_QUESTIONS.map(q => q.key);
const slugify = (s) => s.replace(/[^a-z0-9]+/gi, '-').toLowerCase();

const VisitorHome = () => {
  const { subdomain } = useParams();
  const [customer, setCustomer] = useState(null);
  const [collaterals, setCollaterals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    countryCode: '+91',
    phone: '',
    email: '',
    selectedCollaterals: [],
    acceptedTerms: false,
    // New fields for Max mode
    infoMode: 'minimal', // 'minimal' or 'max'
    fullName: '',
    companyName: '',
    designation: '',
    city: '',
    notes: '',
    jobLevel: '',
    bant_budget: '',
    bant_role: '',
    bant_committee: '',
    bant_need_stage: '',
    bant_existing_solution: '',
    bant_timeline: '',
  });
  const [termsOpen, setTermsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [voiceResult, setVoiceResult] = useState(null); // {transcript, fields} pending confirmation

  const applyVoiceResult = () => {
    const f = (voiceResult && voiceResult.fields) || {};
    setFormData((prev) => ({
      ...prev,
      fullName: f.full_name || prev.fullName,
      companyName: f.company_name || prev.companyName,
      designation: f.designation || prev.designation,
      city: f.city || prev.city,
      notes: f.notes ? ((prev.notes ? prev.notes + ' ' : '') + f.notes).slice(0, 200) : prev.notes,
    }));
    setVoiceResult(null);
    toast.success('Fields filled — please review');
  };
  const [showBant, setShowBant] = useState(false);

  const JOB_LEVELS = [
    'Business Professional',
    'C-Level',
    'Director',
    'IT Professional',
    'Manager',
  ];

  useEffect(() => {
    if (!subdomain) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    const fetchCustomerData = async () => {
      try {
        const customerResponse = await api.get(`/public/customer/${subdomain}`);
        setCustomer(customerResponse.data);

        const collateralsResponse = await api.get(`/public/collaterals/${customerResponse.data.customer_id}`);
        setCollaterals(collateralsResponse.data);
      } catch (error) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomerData();
  }, [subdomain]);

  useEffect(() => {
    document.title = customer ? `${customer.business_name} | LeadCrew` : 'LeadCrew';
    return () => { document.title = 'LeadCrew'; };
  }, [customer]);


  const handleSubmit = async () => {
    if (formData.selectedCollaterals.length === 0) {
      toast.error('Please select at least one material');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        whatsapp_number: `${formData.countryCode}${formData.phone}`,
        email: formData.email,
        selected_collaterals: formData.selectedCollaterals,
        info_mode: formData.infoMode === 'scan' ? 'max' : formData.infoMode,
      };

      // Include additional fields if Max mode or Scan mode (scan pre-fills max fields)
      if (formData.infoMode === 'max' || scanComplete) {
        submitData.full_name = formData.fullName;
        submitData.company_name = formData.companyName;
        submitData.designation = formData.designation;
        submitData.city = formData.city;
        submitData.notes = formData.notes;
        submitData.job_level = formData.jobLevel;
      }

      // Optional BANT answers — included in every mode, only when answered.
      BANT_KEYS.forEach((k) => { if (formData[k]) submitData[k] = formData[k]; });

      await api.post(`/public/submit?customer_id=${customer.customer_id}`, submitData);
      
      setStep(4);
      toast.success('Materials sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCollateral = (collateralId) => {
    setFormData((prev) => ({
      ...prev,
      selectedCollaterals: prev.selectedCollaterals.includes(collateralId)
        ? prev.selectedCollaterals.filter((id) => id !== collateralId)
        : [...prev.selectedCollaterals, collateralId],
    }));
  };

  const handleCardExtracted = (data) => {
    // Parse phone: split a KNOWN country code from the local number without
    // ever dropping a digit of the local part.
    let raw = (data.phone || '').replace(/[^\d+]/g, ''); // keep only + and digits
    // Normalize international prefix 00xx -> +xx
    if (raw.startsWith('00')) raw = '+' + raw.slice(2);

    let countryCode = '+91';
    if (raw.startsWith('+')) {
      // Match the LONGEST known country code prefix (e.g. +91 before +9).
      const matched = [...COUNTRY_CODES]
        .sort((a, b) => b.code.length - a.code.length)
        .find(c => raw.startsWith(c.code));
      if (matched) {
        countryCode = matched.code;
        raw = raw.slice(matched.code.length);
      } else {
        raw = raw.replace(/^\+/, ''); // unknown code: keep digits, default +91
      }
    }

    let phone = raw.replace(/\D/g, '');
    // Indian numbers: strip a national trunk '0' and cap at 10 digits.
    if (countryCode === '+91') {
      if (phone.length === 11 && phone.startsWith('0')) phone = phone.slice(1);
      if (phone.length > 10 && phone.startsWith('91')) phone = phone.slice(2);
      phone = phone.slice(0, 10);
    }

    setFormData(prev => ({
      ...prev,
      infoMode: 'max',
      fullName: data.name || prev.fullName,
      companyName: data.company || prev.companyName,
      designation: data.designation || prev.designation,
      phone: phone || prev.phone,
      countryCode,
      email: data.email || prev.email,
      city: data.city || (data.address ? data.address.split(',').pop().trim() : '') || prev.city,
      notes: [data.website, data.address].filter(Boolean).join(' | ').slice(0, 200) || prev.notes,
    }));
    setScanComplete(true);
    toast.success('Card details extracted! Please review below.');
  };

  const getIcon = (type) => {
    switch (type) {
      case 'pdf':
        return <FileText className="w-8 h-8" />;
      case 'youtube':
        return <PlayCircle className="w-8 h-8" />;
      case 'image':
        return <ImageIcon className="w-8 h-8" />;
      default:
        return <FileText className="w-8 h-8" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="booth-not-found">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="w-10 h-10 text-slate-400" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-3">Booth Not Found</h1>
          <p className="text-slate-500 text-lg">
            {subdomain 
              ? `No booth found for "${subdomain}". Please check the URL and try again.`
              : 'Please visit a specific booth URL to get started.'}
          </p>
        </div>
      </div>
    );
  }

  const phoneDigits = (formData.phone || '').replace(/\D/g, '');
  const phoneNeedsCheck = phoneDigits.length > 0 && (
    phoneDigits.length !== 10 ||
    (formData.countryCode === '+91' && !/^[6-9]/.test(phoneDigits))
  );

  return (
    <BrandProvider customer={customer}>
      <div className="min-h-screen flex flex-col items-center justify-center p-2 md:p-8 bg-slate-50">
        <div className="w-full max-w-lg">
          {/* Progress Indicator */}
          {step < 4 && (
            <div className="mb-2 md:mb-6 flex items-center justify-center gap-2">
              {[1, 2, 3].map((num) => (
                <div
                  key={num}
                  className={`h-1.5 md:h-2 rounded-full transition-all ${
                    num === step ? 'w-6 md:w-8 bg-[var(--brand-color)]'
                      : num < step ? 'w-1.5 md:w-2 bg-[var(--brand-color)]'
                      : 'w-1.5 md:w-2 bg-slate-300'
                  }`}
                  data-testid={`progress-step-${num}`}
                />
              ))}
              <span className="ml-1.5 text-xs md:text-sm text-slate-600">
                Step {step} of 3
              </span>
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* Step 1: Welcome */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
                data-testid="welcome-step"
              >
                <div className="p-8 text-center border-b border-slate-100">
                  {customer.logo_url ? (
                    <img
                      src={customer.logo_url}
                      alt={customer.business_name}
                      className="h-16 mx-auto mb-6 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-20 h-20 bg-slate-200 rounded-lg mx-auto mb-6 flex items-center justify-center">
                      <span className="text-slate-500 text-sm">Logo</span>
                    </div>
                  )}
                  <h1
                    className="text-3xl md:text-4xl font-bold font-heading mb-3"
                    style={{ color: customer.primary_color }}
                  >
                    {customer.welcome_headline}
                  </h1>
                  <p className="text-slate-600 text-lg leading-relaxed">
                    {customer.welcome_message}
                  </p>
                </div>
                <div className="p-8">
                  <button
                    onClick={() => setStep(2)}
                    className="w-full h-14 rounded-full text-white font-semibold shadow-lg text-lg hover:opacity-90 transition-all"
                    style={{ backgroundColor: customer.primary_color }}
                    data-testid="get-started-button"
                  >
                    Get Started
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Contact Form */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
                data-testid="contact-step"
              >
                <div className="px-4 py-3 md:p-8 border-b border-slate-100 text-center">
                  <h2 className="text-lg md:text-2xl font-bold text-slate-900 font-heading">Your Contact Information</h2>
                  <p className="text-slate-600 mt-0.5 text-xs md:text-sm">We&apos;ll send the selected collateral to your email</p>
                  
                  {/* Info Mode Radio Buttons */}
                  <div className="flex justify-center gap-4 mt-1.5 md:mt-2 flex-wrap">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="infoMode" value="minimal" checked={formData.infoMode === 'minimal'} onChange={(e) => { setFormData({ ...formData, infoMode: e.target.value }); setScanComplete(false); }} className="w-4 h-4 text-blue-600" data-testid="info-mode-minimal" />
                      <span className={`text-sm font-medium ${formData.infoMode === 'minimal' ? 'text-slate-900' : 'text-slate-500'}`}>Minimal</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="infoMode" value="max" checked={formData.infoMode === 'max' && !scanComplete} onChange={(e) => { setFormData({ ...formData, infoMode: e.target.value }); setScanComplete(false); }} className="w-4 h-4 text-blue-600" data-testid="info-mode-max" />
                      <span className={`text-sm font-medium ${formData.infoMode === 'max' && !scanComplete ? 'text-slate-900' : 'text-slate-500'}`}>Max</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="infoMode" value="scan" checked={formData.infoMode === 'scan' || scanComplete} onChange={() => { setFormData({ ...formData, infoMode: 'scan' }); setScanComplete(false); }} className="w-4 h-4 text-blue-600" data-testid="info-mode-scan" />
                      <span className={`text-sm font-medium flex items-center gap-1 ${formData.infoMode === 'scan' || scanComplete ? 'text-slate-900' : 'text-slate-500'}`}>
                        <Camera className="w-3.5 h-3.5" /> Scan Card
                      </span>
                    </label>
                  </div>
                </div>
                <div className="px-4 py-3 md:p-8 space-y-2 md:space-y-3 max-h-[60vh] overflow-y-auto">
                  {/* Card Scanner - shown when scan mode is active and not yet extracted */}
                  {formData.infoMode === 'scan' && !scanComplete && (
                    <Suspense fallback={<div className="text-center py-8 text-sm text-slate-500">Loading scanner…</div>}>
                      <CardScanner onExtracted={handleCardExtracted} brandColor={customer.primary_color} />
                    </Suspense>
                  )}

                  {/* Show form fields for minimal, max, or after scan extraction */}
                  {(formData.infoMode !== 'scan' || scanComplete) && (
                    <>
                  <div className="space-y-0.5 md:space-y-1">
                    <Label htmlFor="phone" className="text-xs md:text-sm">WhatsApp Number</Label>
                    <div className="flex gap-2">
                      <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
                        <SelectTrigger className="w-[76px] h-9 md:h-10 text-xs md:text-sm" data-testid="country-code-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRY_CODES.map((item) => (
                            <SelectItem key={item.code} value={item.code}>{item.code}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input id="phone" type="tel" placeholder="9876543210" className={`flex-1 h-9 md:h-10 text-sm md:text-base ${phoneNeedsCheck ? 'border-amber-400 focus-visible:ring-amber-400' : ''}`} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })} required data-testid="phone-input" />
                    </div>
                    {phoneNeedsCheck && (
                      <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="phone-check-warning">
                        <AlertCircle className="w-3 h-3" /> Please check this number{formData.countryCode === '+91' ? ' — Indian mobile numbers are 10 digits starting 6–9' : ''}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-0.5 md:space-y-1">
                    <Label htmlFor="email" className="text-xs md:text-sm">Email Address</Label>
                    <Input id="email" type="email" placeholder="you@example.com" className="h-9 md:h-10 text-sm md:text-base" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="email-input" />
                  </div>

                  {/* Additional fields for Max mode or after card scan extraction */}
                  {(formData.infoMode === 'max' || scanComplete) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-3 pt-3 border-t border-slate-200"
                    >
                      <div className="flex flex-col items-center gap-1 pb-1">
                        <VoiceInput
                          testId="voice-smart-fill"
                          label="Fill all fields by voice"
                          title="Say your name, company, role and city"
                          endpoint="/public/smart-voice-fill"
                          onResult={(data) => {
                            const f = (data && data.fields) || {};
                            if (!(data && data.transcript) && !f.full_name && !f.company_name && !f.designation && !f.city && !f.notes) {
                              toast.error("Didn't catch that — please try again");
                              return;
                            }
                            setVoiceResult({ transcript: (data && data.transcript) || '', fields: f });
                          }}
                        />
                        <span className="text-xs text-slate-400">Speak in any language — e.g. "I'm Ravi from BigCorp, VP Sales, Mumbai"</span>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="fullName" className="text-sm">Full Name <span className="text-red-500">*</span></Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="fullName"
                          type="text"
                          placeholder="John Doe"
                          className="h-10 text-base flex-1"
                          value={formData.fullName}
                          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                          required
                          data-testid="full-name-input"
                        />
                        <VoiceInput testId="voice-full-name" title="Dictate full name" onTranscript={(t) => setFormData((prev) => ({ ...prev, fullName: t.replace(/\.$/, '') }))} />
                      </div>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="companyName" className="text-sm">Company Name <span className="text-red-500">*</span></Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="companyName"
                          type="text"
                          placeholder="Acme Inc"
                          className="h-10 text-base flex-1"
                          value={formData.companyName}
                          onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                          required
                          data-testid="company-name-input"
                        />
                        <VoiceInput testId="voice-company-name" title="Dictate company name" onTranscript={(t) => setFormData((prev) => ({ ...prev, companyName: t.replace(/\.$/, '') }))} />
                      </div>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="designation" className="text-sm">Designation <span className="text-red-500">*</span></Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="designation"
                          type="text"
                          placeholder="Software Engineer"
                          className="h-10 text-base flex-1"
                          value={formData.designation}
                          onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                          required
                          data-testid="designation-input"
                        />
                        <VoiceInput testId="voice-designation" title="Dictate designation" onTranscript={(t) => setFormData((prev) => ({ ...prev, designation: t.replace(/\.$/, '') }))} />
                      </div>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="city" className="text-sm">City <span className="text-red-500">*</span></Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="city"
                          type="text"
                          placeholder="Mumbai"
                          className="h-10 text-base flex-1"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          required
                          data-testid="city-input"
                        />
                        <VoiceInput testId="voice-city" title="Dictate city" onTranscript={(t) => setFormData((prev) => ({ ...prev, city: t.replace(/\.$/, '') }))} />
                      </div>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="jobLevel" className="text-sm">Job Level <span className="text-red-500">*</span></Label>
                        <Select
                          value={formData.jobLevel}
                          onValueChange={(value) => setFormData({ ...formData, jobLevel: value })}
                        >
                          <SelectTrigger className="h-10 text-base" data-testid="job-level-select">
                            <SelectValue placeholder="Select job level" />
                          </SelectTrigger>
                          <SelectContent>
                            {JOB_LEVELS.map((level) => (
                              <SelectItem key={level} value={level}>
                                {level}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </motion.div>
                  )}

                  {/* Notes field - available in both Minimal and Max modes, non-mandatory */}
                  <div className="space-y-0.5 md:space-y-1">
                    <Label htmlFor="notes" className="text-xs md:text-sm">
                      Notes
                      <span className="text-slate-400 text-xs ml-1">({formData.notes.length}/200)</span>
                    </Label>
                    <div className="flex items-start gap-2">
                      <textarea
                        id="notes"
                        placeholder="Any specific questions..."
                        className="w-full h-12 md:h-16 px-3 py-1.5 text-sm md:text-base border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none flex-1"
                        value={formData.notes}
                        onChange={(e) => {
                          if (e.target.value.length <= 200) {
                            setFormData({ ...formData, notes: e.target.value });
                          }
                        }}
                        maxLength={200}
                        data-testid="notes-input"
                      />
                      <VoiceInput testId="voice-notes" title="Dictate notes" onTranscript={(t) => setFormData((prev) => ({ ...prev, notes: ((prev.notes ? prev.notes + ' ' : '') + t).slice(0, 200) }))} />
                    </div>
                  </div>

                  {/* Optional BANT qualifying questions — available in all modes */}
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setShowBant((v) => !v)}
                      className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                      data-testid="toggle-bant-button"
                    >
                      {showBant ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      {showBant ? 'Hide questions' : 'Add questions'}
                    </button>

                    {showBant && (
                      <div className="space-y-3 pt-1" data-testid="bant-questions">
                        {BANT_QUESTIONS.map((q) => (
                          <div key={q.key} className="space-y-1">
                            <Label className="text-xs md:text-sm">{q.label}</Label>
                            <div className="flex flex-wrap gap-1.5">
                              {q.options.map((opt) => {
                                const selected = formData[q.key] === opt;
                                return (
                                  <button
                                    type="button"
                                    key={opt}
                                    onClick={() => setFormData((prev) => ({ ...prev, [q.key]: selected ? '' : opt }))}
                                    className={`px-3 py-1.5 rounded-full border text-xs md:text-sm transition-colors ${selected ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                                    style={selected ? { backgroundColor: customer.primary_color } : undefined}
                                    data-testid={`bant-${q.key}-${slugify(opt)}`}
                                  >
                                    {opt}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="terms"
                      checked={formData.acceptedTerms}
                      onCheckedChange={(checked) => setFormData({ ...formData, acceptedTerms: checked })}
                      className="mt-0.5"
                      data-testid="terms-checkbox"
                    />
                    <label htmlFor="terms" className="text-xs md:text-sm text-slate-600 leading-snug">
                      I accept the{' '}
                      <button type="button" onClick={(e) => { e.preventDefault(); setTermsOpen(true); }} className="text-blue-600 hover:text-blue-700 underline font-medium" data-testid="terms-link">terms and conditions</button>
                    </label>
                  </div>
                    </>
                  )}
                </div>
                <div className="px-4 py-2.5 md:px-5 md:py-3 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="h-9 md:h-10 px-4 md:px-5"
                    data-testid="back-button"
                  >
                    Back
                  </Button>
                  <button
                    onClick={() => {
                      if (!formData.phone || !formData.email) {
                        toast.error('Please fill in all fields');
                        return;
                      }
                      // Validate Max mode fields (notes is optional) - also for scan mode after extraction
                      if (formData.infoMode === 'max' || scanComplete) {
                        if (!formData.fullName || !formData.companyName || !formData.designation || 
                            !formData.city || !formData.jobLevel) {
                          toast.error('Please fill in all required fields');
                          return;
                        }
                      }
                      if (!formData.acceptedTerms) {
                        toast.error('Please accept the terms and conditions to continue');
                        return;
                      }
                      setStep(3);
                    }}
                    className="h-10 px-8 rounded-full text-white font-semibold hover:opacity-90 transition-all disabled:opacity-40"
                    style={{ backgroundColor: customer.primary_color }}
                    disabled={formData.infoMode === 'scan' && !scanComplete}
                    data-testid="continue-button"
                  >
                    Continue
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Collateral Selection */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
                data-testid="collateral-step"
              >
                <div className="px-6 py-4 md:p-8 border-b border-slate-100 text-center">
                  <h2 className="text-xl md:text-2xl font-bold text-slate-900 font-heading">Select Collateral</h2>
                  <p className="text-slate-600 mt-1 text-sm">Choose the collateral you&apos;d like to receive</p>
                </div>
                <div className="px-5 py-4 md:p-8 space-y-2 max-h-96 overflow-y-auto">
                  {collaterals.map((collateral) => (
                    <div
                      key={collateral.collateral_id}
                      onClick={() => toggleCollateral(collateral.collateral_id)}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                        formData.selectedCollaterals.includes(collateral.collateral_id)
                          ? 'border-[var(--brand-color)] bg-[var(--brand-color)]/5'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`collateral-option-${collateral.collateral_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={formData.selectedCollaterals.includes(collateral.collateral_id)}
                          onCheckedChange={(e) => {
                            e.stopPropagation();
                            toggleCollateral(collateral.collateral_id);
                          }}
                          className="h-4 w-4 pointer-events-none"
                        />
                        <div
                          className="flex-shrink-0"
                          style={{ color: customer.primary_color }}
                        >
                          <div className="scale-75">
                            {getIcon(collateral.type)}
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900 text-sm">{collateral.title}</h3>
                          <p className="text-xs text-slate-500 capitalize">{collateral.type}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-3 md:p-6 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="h-10 px-5"
                    data-testid="back-to-contact-button"
                  >
                    Back
                  </Button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || formData.selectedCollaterals.length === 0}
                    className="h-10 px-8 rounded-full text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50"
                    style={{ backgroundColor: customer.primary_color }}
                    data-testid="send-materials-button"
                  >
                    {submitting ? 'Sending...' : 'Send to Me'}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Success */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
                data-testid="success-step"
              >
                <div className="p-12 text-center">
                  <div
                    className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                    style={{ backgroundColor: `${customer.primary_color}20` }}
                  >
                    <CheckCircle className="w-12 h-12" style={{ color: customer.primary_color }} />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900 font-heading mb-3">
                    Selected collateral sent successfully!
                  </h2>
                  <p className="text-slate-600 text-lg mb-8">
                    {customer.thank_you_message ? customer.thank_you_message.replace("We'll be in touch soon.", "").trim() : "Thank you for your interest!"}
                  </p>
                  
                  <div className="bg-slate-50 rounded-xl p-6 mb-8 text-left">
                    <p className="text-sm font-semibold text-slate-700 mb-3">Your Collateral (tap to open):</p>
                    <div className="space-y-2">
                      {formData.selectedCollaterals.map((collateralId) => {
                        const collateral = collaterals.find((c) => c.collateral_id === collateralId);
                        if (!collateral) return null;
                        return (
                          <a
                            key={collateralId}
                            href={collateral.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 -mx-2 rounded-lg hover:bg-white transition-colors group"
                            data-testid={`success-collateral-link-${collateralId}`}
                          >
                            {getIcon(collateral.type)}
                            <span className="text-slate-700 group-hover:text-blue-600 group-hover:underline flex-1">{collateral.title}</span>
                            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                  
                  {customer.website_url && (
                    <a
                      href={customer.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block h-12 px-8 rounded-full text-white font-semibold hover:opacity-90 transition-all leading-[3rem]"
                      style={{ backgroundColor: customer.primary_color }}
                      data-testid="learn-more-button"
                    >
                      Learn More
                    </a>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Terms and Conditions Dialog */}
        <Dialog open={!!voiceResult} onOpenChange={(open) => { if (!open) setVoiceResult(null); }}>
          <DialogContent className="max-w-md" data-testid="voice-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Review before filling</DialogTitle>
              <DialogDescription>Check what we heard, then fill your details or cancel.</DialogDescription>
            </DialogHeader>
            {voiceResult && (
              <div className="mt-2 space-y-4">
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1">We heard</p>
                  <p className="text-sm text-slate-700 italic bg-slate-50 rounded-lg p-3" data-testid="voice-confirm-transcript">"{voiceResult.transcript || '\u2014'}"</p>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500">Detected fields (in English) — edit if needed</p>
                  {[
                    ['Full Name', 'full_name'],
                    ['Company', 'company_name'],
                    ['Designation', 'designation'],
                    ['City', 'city'],
                  ].map(([label, key]) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs text-slate-500">{label}</Label>
                      <Input
                        className="h-9 text-sm"
                        value={voiceResult.fields[key] || ''}
                        placeholder="not mentioned"
                        onChange={(e) => setVoiceResult((prev) => ({ ...prev, fields: { ...prev.fields, [key]: e.target.value } }))}
                        data-testid={`voice-edit-${key.replace(/_/g, '-')}`}
                      />
                    </div>
                  ))}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Notes</Label>
                    <textarea
                      className="w-full h-14 px-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      value={voiceResult.fields.notes || ''}
                      placeholder="not mentioned"
                      onChange={(e) => setVoiceResult((prev) => ({ ...prev, fields: { ...prev.fields, notes: e.target.value } }))}
                      data-testid="voice-edit-notes"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setVoiceResult(null)} data-testid="voice-confirm-cancel">Cancel</Button>
                  <Button className="flex-1 text-white" style={{ backgroundColor: customer.primary_color }} onClick={applyVoiceResult} data-testid="voice-confirm-apply">Fill fields</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Terms and Conditions</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4 text-slate-700 leading-relaxed">
              <p className="text-base">
                By ticking on the check box, you acknowledge and agree that we may collect and process certain personal information (such as your name, contact details, and professional details) to manage your participation and enhance your experience.
              </p>
              <p className="text-base">
                You consent to your information being used to inform you about relevant products, services, and upcoming initiatives, and to it being shared with event sponsors and their authorized partners for marketing and promotional purposes, in accordance with applicable data protection laws and our Privacy Policy.
              </p>
              <p className="text-base">
                You confirm that the information provided is accurate and that you are authorized to share it.
              </p>
              <p className="text-base">
                We implement industry-standard security measures to safeguard your data from unauthorized access or disclosure.
              </p>
              <div className="pt-4 border-t border-slate-200">
                <Button
                  onClick={() => {
                    setFormData({ ...formData, acceptedTerms: true });
                    setTermsOpen(false);
                  }}
                  className="w-full h-12"
                  style={{ backgroundColor: customer.primary_color }}
                  data-testid="accept-terms-button"
                >
                  I Accept
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </BrandProvider>
  );
};

export default VisitorHome;
